import * as React from 'react';

import { Alert, AlertColor, AppBar, Badge, Box, IconButton, Link, Paper, Snackbar, Stack, Tab, Tabs, Toolbar, Tooltip, Typography } from '@mui/material';
import HelpTooltip from '../help/HelpTooltip';
import { grey } from '@mui/material/colors';
import { useLocation, useNavigate, Routes, Route, Navigate } from "react-router-dom";

/* Any doubt for compilation ? https://refine.dev/blog/react-svg/#importing-as-a-react-component */
import { ReactComponent as LogoBlack } from '../images/Logo_Lectra_def_CMJN_black-red.svg';
import { ReactComponent as LogoWhite } from '../images/Logo_Lectra_def_CMJN_white-red.svg';


import { SearchParameters } from '../redux/cutterSearchAPI';
import { setCredentials } from '../redux/userAuth';

import { Contexte, useAuthenticationValue } from '@lectra/react-authentication';
import { Download, PowerSettingsNew, HdrWeak, HdrStrong, Help, HelpOutline, InfoOutlined} from '@mui/icons-material';

import { useAppDispatch, useAppSelector } from '../appHooks';
import Bookmarks from '../bookmarks/Bookmarks';
import CuttersSearch from '../search/CuttersSearch';
import ConnexionStats from '../stats/ConnexionStats';
import { RootState } from '../store';
import AppContextCtrl from './AppContextCtrl';
import LogToDB from '../types/dbLogs';
import { toggleTheme } from '../redux/themePreference';
import { toggleHelpVisibility } from '../redux/helpVisibility';
import HelpIcon from '../help/HelpIcon';
import { SnackbarProvider } from '../common/SnackbarContext';
import ReleaseNotesSnackbar from '../common/ReleaseNotesSnackbar';
import { toggleReleaseNotes } from '../redux/releaseNotesVisibility';
import useVersionChecker from '../hooks/useVersionChecker';


export const ConnexionTabs: React.FC = () => {
	// Authenticate API requests
	const dispatch = useAppDispatch();
	const userContext = useAuthenticationValue();

	// Global Snackbar state
	const [snackbarOpen, setSnackbarOpen] = React.useState(false);
	const [snackbarMessage, setSnackbarMessage] = React.useState('');
	const [snackbarDuration, setSnackbarDuration] = React.useState(1000);
	const [snackbarSeverity, setSnackbarSeverity] = React.useState<AlertColor | undefined>(undefined);

	const handleShowSnackbar = (message: string, duration: number = 1000, severity?: AlertColor): void => {
		setSnackbarMessage(message);
		setSnackbarDuration(duration);
		setSnackbarSeverity(severity);
		setSnackbarOpen(true);
	};

	// Tabs navigation**
	const navigate = useNavigate();
	const location = useLocation();

	const authUser: Contexte = useAppSelector<Contexte>((state: RootState) => state.userStateReducer);
	const search: SearchParameters = useAppSelector<SearchParameters>((state: RootState) => state.persistedSearchReducer);
	const themeMode = useAppSelector((state: RootState) => state.persistedThemePreferenceReducer.mode);
	const isHelpVisible = useAppSelector((state: RootState) => state.persistedHelpVisibilityReducer.isVisible);

	// Determine current tab based on pathname
	const currentTab = React.useMemo(() => {
		const path = location.pathname.split('?')[0];
		return path === "/" ? "/search" : path;
	}, [location.pathname]);

	React.useEffect(() => {
		dispatch(setCredentials(userContext));
	}, [dispatch, userContext]);

	// Notify users coming from the old domain to update their bookmark.
	// Guard on userContext.user: AuthenticationProvider may redirect to Auth0 before auth is
	// confirmed, tearing down the component. We must wait until the user is authenticated
	// (and the Auth0 round-trip is done) before consuming the flag.
	React.useEffect(() => {
		if (userContext?.user && sessionStorage.getItem('redirectedFromOldDomain') === '1') {
			sessionStorage.removeItem('redirectedFromOldDomain');
			handleShowSnackbar('New address: deviceconn.lectra.com — please update your bookmark!', 8000, 'info');
		}
	}, [userContext?.user]);

	// Check for version updates once auth is established
	useVersionChecker();

	// Force load of ETL LastUpdates
	// if (!appContextGet.isUninitialized) dispatch(appContextGet.util.invalidateTags(['ETLUpdates']));

	setTimeout(
		() => {
			// dispatch(appContextApi.util.invalidateTags(["ETLUpdates", "Keywords"]));
		},
		// ~ every 30 minutes, round of the hour
		(30 - new Date().getMinutes()) * 60 * 1000 // 60 * 1000 ms = 1mn
		// 1 * 60 * 1000
	);

	return (
		<SnackbarProvider onShowSnackbar={handleShowSnackbar}>
			<Paper sx={{ maxWidth: 936, marginX: 'auto', overflow: 'hidden', marginY: 1 }}>
				{/* ----------------------------------------------- */}
				{/*                     HEADER                      */}
				{/* ----------------------------------------------- */}
				<input type="hidden" name="email_user" placeholder="Email" value={authUser?.user?.email} />
				{/* 
            <Alert severity="warning" sx={{
                fontSize: '1rem', "& .MuiAlert-icon": {
                    fontSize: '1.5rem'
                }
            }} >This application will be switched off on 7th of June 2024 on demand of CyberSecurity Architect</Alert>
            */}
				<AppBar position="static" elevation={0} sx={{
					zIndex: 0, margin: 0, backgroundColor: 'transparent', color: 'inherit'
				}}>
					<Toolbar disableGutters sx={{ justifyContent: 'space-between', paddingX: 0 }} >
						<Tabs value={currentTab} textColor="inherit"
							role="navigation" onChange={(e: React.SyntheticEvent, tabValue: string) => {
								const queryString = location.search || '';
								navigate(tabValue + queryString);
							}} >
							<Tab label={
								<Stack direction="row" alignItems="center">
									Search
									<HelpIcon helpContext="tab-search" placement="bottom" />
								</Stack>
							} data-tut="tabSearch" value='/search' aria-selected />
							<Tab label={
								<Stack direction="row" alignItems="center">
									<Badge badgeContent={search.favorites.length}
										color="primary" data-tut="tabBookmarks_badge" anchorOrigin={{
											vertical: 'top',
											horizontal: 'right',
										}} >Bookmarks&nbsp;&nbsp;
									</Badge>
									<HelpIcon helpContext="tab-bookmarks" placement="bottom" />
								</Stack>
							} data-tut="tabBookmarks" value='/bookmarks' />
							{/* SEPARATOR */}
							<span style={{ width: 30 }} />
							<Tab label={
								<Stack direction="row" alignItems="center">
									STATS
									<HelpIcon helpContext="tab-stats" placement="bottom" />
								</Stack>
							} value='/stats' data-tut="tabStats" />

							{/* <Tab label="Usage" disabled /> */}
						</Tabs>
						<Stack direction="row" spacing={1} alignItems='center' sx={{ padding: '30,10' }} >
							<HelpTooltip enterTouchDelay={0} placement='bottom-start' enterNextDelay={5000}
								title={
									<>
										<Typography sx={{ lineHeight: '1rem', whiteSpace: 'pre' }}>
											Download EventManager 8.0.4 <br />Latest version (valid for all cutters)
										</Typography>
										<Typography sx={{ lineHeight: '0.8rem', fontSize: '0.8rem' }}>
											<span>Release Note : 8.0.4</span>
											<ul>
												<li>chore: [VECPIL-2544] New identifiers workOrderID, segmentID for Valia R5</li>
												<li>chore: [VECPIL-2434] Remove reports from Transit after upload</li>
											</ul>
											<br />
											<span>Release Note : 8.0.3 hotfix 7</span>
											<ul>
												<li>fix: [LEATHSUI-465] Ignore connection warning for Time.asp request failure</li>
												<li>fix: [VIRPIL-877] Complete Reporting xml file with Section Node</li>
											</ul>
											<br />
										</Typography>
									</>
								}>
								<IconButton data-tut="downloadEventManager" href={`${process.env.PUBLIC_URL}/EventManager/latest`} download>
									<Download titleAccess='' color='inherit' />
								</IconButton>
							</HelpTooltip>
							<AppContextCtrl />
							<HelpIcon helpContext="refresh-data" placement="bottom" />
							<Tooltip title={`Switch to ${themeMode === 'light' ? 'dark' : 'light'} mode`}>
								<IconButton onClick={() => {
									const newMode = themeMode === 'light' ? 'dark' : 'light';
									void LogToDB('Theme', newMode);
									dispatch(toggleTheme());
								}}>
									{themeMode === 'light' ? <HdrStrong color='inherit' /> : <HdrWeak color='inherit' />}
								</IconButton>
							</Tooltip>
							<Tooltip title={isHelpVisible ? "Hide Help" : "Show Help"}>
								<IconButton size="small" color={isHelpVisible ? 'primary' : 'default'}
									onClick={() => {
										const newState = isHelpVisible ? 'hide' : 'show';
										void LogToDB('SwitchHelp', newState);
										dispatch(toggleHelpVisibility());
									}}  >
									{isHelpVisible ? <Help color='info' /> : <HelpOutline color='inherit' />}
								</IconButton>
							</Tooltip>
							{isHelpVisible ? <Tooltip title="What's new in this application">
										<IconButton size="small" onClick={() => dispatch(toggleReleaseNotes())}>
											<InfoOutlined  sx={{ fontSize: 'small', color: 'info.main' }} />
										</IconButton>
									</Tooltip> : null}
							<IconButton onClick={e => authUser.actions.logout()}>
								<PowerSettingsNew titleAccess='Sign out' color='inherit' />
							</IconButton>
							{themeMode === 'light' ? <LogoBlack width="100px" /> : <LogoWhite width="100px" />}
						</Stack>
					</Toolbar>
				</AppBar>

				{/* Routes Content */}
				<Routes>
					<Route path="/" element={<Navigate to="/search" replace />} />
					<Route path="/search" element={<CuttersSearch />} />
					<Route path="/bookmarks" element={<Bookmarks />} />
					<Route path="/stats" element={<ConnexionStats />} />
				</Routes>

				{/* ----------------------------------------------- */}
				{/*                     FOOTER                      */}
				{/* ----------------------------------------------- */}
				<Box component="footer" >
					<Typography fontSize='65%' color={grey[400]} align="center">
						Some datasources can be temporary or durably erroneous, incomplete or estimated. We work on it ! (Really ;-)<br />
					</Typography>
					<Typography fontSize='65%' color={grey[500]} align="center">
						Line Manufacturing - CFactory {new Date().getFullYear()}&nbsp;
						<Link color="primary" href="https://mylectra.com/" target="_blank" >
							www.lectra.com
						</Link>{' '}
						{process.env.REACT_APP_NAME?.replace('_', ' ')} {process.env.REACT_APP_VERSION}
					</Typography>
				</Box>

				{/* Global Snackbar : used essentially by CopyToClipBoard component */}
				<Snackbar
					anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
					autoHideDuration={snackbarDuration}
					onClose={() => setSnackbarOpen(false)}
					open={snackbarOpen}
					message={snackbarSeverity ? undefined : snackbarMessage}
				>
					{snackbarSeverity ? (
						<Alert severity={snackbarSeverity} onClose={() => setSnackbarOpen(false)} variant="filled" sx={{ width: '100%' }}>
							{snackbarMessage}
						</Alert>
					) : undefined}
				</Snackbar>

				<ReleaseNotesSnackbar />
			</Paper>
		</SnackbarProvider>
	);
}

export default ConnexionTabs;


